/**
 * @fileoverview Current-principal query hook.
 * @description Wraps GET /identity/me, for surfacing account details (tenant, role level, subject
 * id) that the login/verify-otp response itself doesn't carry. Used by many components at once,
 * so a result stays fresh for 30 seconds: mounting another component (or focusing the tab again)
 * inside that window reuses it instead of re-fetching it with its capabilities pages, and one
 * fetch is shared by every observer. Login seeds it, and logout clears it. A Super Admin can change
 * capabilities while this user is signed in, so once it is stale it re-fetches when the tab regains
 * focus — and on page navigation (throttled the same way) and on a `403 / 2008`, see
 * useKeepAccessCurrent and the response interceptor.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useQuery } from '@tanstack/react-query'

import { getCurrentPrincipal } from '@/features/auth/services/session.service'
import { CURRENT_SESSION_QUERY_KEY } from '@/shared/constants/session-query-keys.constants'

export { CURRENT_SESSION_QUERY_KEY }

// How long a result counts as current: the throttle for the focus and navigation re-fetches.
const SESSION_STALE_TIME_MS = 30 * 1000

/**
 * @description Fetches the signed-in user's own principal.
 * @returns {ReturnType<typeof useQuery>} The query object (data, isPending, error, ...).
 */
export function useCurrentSession() {
  return useQuery({
    queryKey: CURRENT_SESSION_QUERY_KEY,
    queryFn: getCurrentPrincipal,
    staleTime: SESSION_STALE_TIME_MS,
    refetchOnWindowFocus: true,
  })
}
